import { useAdminPresence } from '@/hooks/useRealtime';
import { useAuthStore } from '@/stores/authStore';
import { formatDistanceToNow } from 'date-fns';

interface AdminPresenceIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

export default function AdminPresenceIndicator({
  showDetails = false,
  className = '',
}: AdminPresenceIndicatorProps) {
  const { user } = useAuthStore();
  const { onlineAdmins } = useAdminPresence(user?.id, user?.email);

  // Filter out current user
  const otherAdmins = onlineAdmins.filter((admin) => admin.userId !== user?.id);

  if (otherAdmins.length === 0 && !showDetails) {
    return null;
  }

  return (
    <div className={className}>
      {showDetails ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Online Admins ({onlineAdmins.length})
          </h3>

          {onlineAdmins.length === 0 ? (
            <p className="text-sm text-gray-500">No other admins online</p>
          ) : (
            <ul className="space-y-2">
              {onlineAdmins.map((admin) => (
                <li
                  key={admin.userId}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
                      <span className="text-brand font-medium text-xs">
                        {admin.userName?.charAt(0).toUpperCase() || 'A'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {admin.userName}
                        {admin.userId === user?.id && (
                          <span className="text-xs text-gray-400 ml-1">(you)</span>
                        )}
                      </p>
                      {admin.currentPage && (
                        <p className="text-xs text-gray-500">
                          Viewing: {admin.currentPage}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatDistanceToNow(new Date(admin.onlineAt), { addSuffix: true })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        // Compact view for header
        <div className="flex items-center gap-1.5">
          <div className="flex -space-x-2">
            {otherAdmins.slice(0, 3).map((admin) => (
              <div
                key={admin.userId}
                className="w-7 h-7 rounded-full bg-brand/10 border-2 border-white flex items-center justify-center"
                title={`${admin.userName} - ${admin.currentPage || 'Online'}`}
              >
                <span className="text-brand font-medium text-xs">
                  {admin.userName?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
            ))}
            {otherAdmins.length > 3 && (
              <div className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center">
                <span className="text-gray-600 font-medium text-xs">
                  +{otherAdmins.length - 3}
                </span>
              </div>
            )}
          </div>
          <span className="text-xs text-gray-500">
            {otherAdmins.length} online
          </span>
        </div>
      )}
    </div>
  );
}
